export const DEFAULT_REFERRAL_COMMISSION_RATE = 10;
export const DEFAULT_REFERRAL_PRICING_PROTECTION_RATE = 15;

const roundCurrency = (value: number) => Math.round((value + Number.EPSILON) * 100) / 100;

export function normalizeReferralRate(
  value: number | string | null | undefined,
  fallback: number,
) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 && parsed <= 100
    ? parsed
    : fallback;
}

export function exactNeutralProtectionRate(commissionRate: number) {
  const rate = normalizeReferralRate(
    commissionRate,
    DEFAULT_REFERRAL_COMMISSION_RATE,
  );
  return rate >= 100 ? null : roundCurrency((rate / (100 - rate)) * 100);
}

export function calculateReferralCommission({
  baseTotalExVat,
  discountRate = 0,
  commissionRate = DEFAULT_REFERRAL_COMMISSION_RATE,
  pricingProtectionRate = DEFAULT_REFERRAL_PRICING_PROTECTION_RATE,
}: {
  baseTotalExVat: number;
  discountRate?: number;
  commissionRate?: number;
  pricingProtectionRate?: number;
}) {
  const base = Math.max(0, Number(baseTotalExVat) || 0),
    discount = normalizeReferralRate(discountRate, 0),
    commission = normalizeReferralRate(
      commissionRate,
      DEFAULT_REFERRAL_COMMISSION_RATE,
    ),
    protection = normalizeReferralRate(
      pricingProtectionRate,
      DEFAULT_REFERRAL_PRICING_PROTECTION_RATE,
    ),
    baseAfterDiscount = roundCurrency(base * (1 - discount / 100)),
    protectedTotalExVat = roundCurrency(
      base * (1 + protection / 100) * (1 - discount / 100),
    ),
    pricingProtectionAmountExVat = roundCurrency(
      protectedTotalExVat - baseAfterDiscount,
    ),
    estimatedCommissionExVat = roundCurrency(
      protectedTotalExVat * (commission / 100),
    );

  return {
    baseTotalExVat: roundCurrency(base),
    baseAfterDiscountExVat: baseAfterDiscount,
    protectedTotalExVat,
    pricingProtectionAmountExVat,
    estimatedCommissionExVat,
    netAfterCommissionExVat: roundCurrency(
      protectedTotalExVat - estimatedCommissionExVat,
    ),
    commissionRate: commission,
    pricingProtectionRate: protection,
    exactNeutralProtectionRate: exactNeutralProtectionRate(commission),
  };
}
